import { Suspense, useEffect, useState } from 'react';

import {
    getAccountsQueryConfig,
    getStateQueryConfig
} from '@api/lendingAPI/queryConfigs';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconCheckDefault } from '@iwoca/icons';
import { Card, Radio, Text } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { LoadingSkeleton } from './Decision/Approved/PageContainer';
import { Decision } from './Decision/Decision';
import { RepaymentReductionForm } from './RepaymentReductionForm/RepaymentReductionForm';
import { useRepaymentReductionSplitTest } from './useRepaymentReductionSplitTest';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { getMaxMonthlyInterestRate } from '../../multiLoanDashboard/dashboardWidgets/loanOverview/useGetLoanOverviewDetails';
import { CreateNewApplicationForm } from '../../multiLoanDashboard/NewOutstandingView/CreateNewApplicationForm/CreateNewApplicationForm';
import { Rate } from '../../ui/rate';
import { BackToDashboard } from '../BackToDashboard';

type TSelectedOption =
    | 'withdraw_additional_funds'
    | 'reduce_minimum_repayments'
    | null;

export const TopUpChoiceForm = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <LoadingSkeleton data-testid="topup-choice-form-loading-skeleton" />
                }
            >
                <Form accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const Form = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const [applicationCreated, setApplicationCreated] = useState(false);
    const { isInRepaymentReductionSplitTest } =
        useRepaymentReductionSplitTest(accountId);

    const { data } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true,
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            )
    });
    const { data: companyTurnover } = useQuery({
        ...getStateQueryConfig(stateKey),
        suspense: true,
        select: (data) =>
            data.data.application?.company?.last_12_months_turnover?.amount
    });

    useEffect(() => {
        tracking.ampli.topupChoiceFormViewed({
            account_id: accountId,
            repayment_reduction_test: isInRepaymentReductionSplitTest
        });
    }, [accountId, isInRepaymentReductionSplitTest]);

    if (applicationCreated) return <Decision accountId={accountId} />;

    const account = data!;
    const outstandingBalance = account.pending_balance!.total!;

    const onApplicationCreated = () => {
        setApplicationCreated(true);
    };

    return (
        <>
            <BackToDashboard />
            <div
                data-testid="topup-choice-form"
                className="flex flex-col-reverse items-start gap-xl min-l:flex-row"
            >
                <Card className="space-y-xl min-l:p-xl">
                    <FormHeader accountId={accountId} />
                    {isInRepaymentReductionSplitTest ? (
                        <TopUpChoice
                            accountId={accountId}
                            onSuccess={onApplicationCreated}
                            outstandingBalance={outstandingBalance}
                            companyTurnover={companyTurnover!}
                        />
                    ) : (
                        <CreateNewApplicationForm
                            accountId={accountId}
                            setApplicationStatus={() => {}}
                            showLoadingWhileSubmitting={false}
                            onSuccess={onApplicationCreated}
                        />
                    )}
                </Card>
                <ExistingLoanSummary account={account} />
            </div>
        </>
    );
};

const TopUpChoice = ({
    accountId,
    onSuccess,
    outstandingBalance,
    companyTurnover
}: {
    accountId: string;
    onSuccess: () => void;
    outstandingBalance: number;
    companyTurnover: number;
}) => {
    const [selectedOption, setSelectedOption] = useState<TSelectedOption>(null);

    return (
        <>
            <RadioGroup
                accountId={accountId}
                setSelectedOption={setSelectedOption}
            />

            {selectedOption === 'withdraw_additional_funds' ? (
                <CreateNewApplicationForm
                    accountId={accountId}
                    setApplicationStatus={() => {}}
                    onSuccess={onSuccess}
                />
            ) : selectedOption === 'reduce_minimum_repayments' ? (
                <RepaymentReductionForm
                    outstandingPrincipal={outstandingBalance}
                    companyTurnover={companyTurnover}
                    onSuccess={onSuccess}
                />
            ) : null}
        </>
    );
};

const FormHeader = ({ accountId }: { accountId: string }) => {
    const { isInRepaymentReductionSplitTest } =
        useRepaymentReductionSplitTest(accountId);

    return (
        <div className="space-y-l">
            <div className="space-y-s">
                {isInRepaymentReductionSplitTest ? (
                    <>
                        <h2 className="m-0 text-2xl">
                            <FormattedMessage
                                id="_.topup_choice.header_title"
                                defaultMessage="Apply for new terms"
                            />
                        </h2>
                        <Text>
                            <FormattedMessage
                                id="_.topup_choice.header_description"
                                defaultMessage="You can apply to reduce your repayments or access additional funds."
                            />
                        </Text>
                    </>
                ) : (
                    <>
                        <h2 className="m-0 text-2xl">
                            <FormattedMessage
                                id="_.apply_topup_loan.title"
                                defaultMessage="Request more funds"
                            />
                        </h2>
                        <Text>
                            <FormattedMessage
                                id="_.topup_choice.withdraw_additional_funds_description"
                                defaultMessage="Access additional cash and start a new term, replacing your current one."
                            />
                        </Text>
                    </>
                )}
            </div>
            <div className="space-y-s">
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.topup_choice.benefit_1"
                            defaultMessage="Applying won’t affect your credit score"
                        />
                    }
                />
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.topup_choice.benefit_2"
                            defaultMessage="No obligation to accept the offer"
                        />
                    }
                />
                <IconWithText
                    message={
                        <FormattedMessage
                            id="_.topup_choice.benefit_3"
                            defaultMessage="The decision won't affect your current loan"
                        />
                    }
                />
            </div>
        </div>
    );
};

const IconWithText = ({ message }: { message: React.ReactNode }) => {
    return (
        <div className="flex flex-row items-center gap-s">
            <IconCheckDefault className="size-[20px] fill-success-mid" />
            <Text className="text-s font-med">{message}</Text>
        </div>
    );
};

const RadioGroup = ({
    accountId,
    setSelectedOption
}: {
    accountId: string;
    setSelectedOption: (value: TSelectedOption) => void;
}) => {
    return (
        <div className="flex flex-col gap-m min-sm:flex-row">
            <Radio.Root
                variant="frame"
                id="withdraw-additional-funds"
                className="flex-1 items-start"
            >
                <Radio.Label>
                    <FormattedMessage
                        id="_.topup_choice.withdraw_additional_funds"
                        defaultMessage="Withdraw additional funds"
                    />
                    <span className="block text-s font-reg">
                        <FormattedMessage
                            id="_.topup_choice.withdraw_additional_funds_description"
                            defaultMessage="Access additional cash and start a new term, replacing your current one."
                        />
                    </span>
                </Radio.Label>
                <Radio.Input
                    name="topup-choice"
                    className="mt-s"
                    onChange={() => {
                        tracking.ampli.radioButtonSelected({
                            account_id: accountId,
                            label: 'Withdraw additional funds'
                        });
                        setSelectedOption('withdraw_additional_funds');
                    }}
                />
            </Radio.Root>
            <Radio.Root
                variant="frame"
                id="reduce-minimum-repayments"
                className="flex-1 items-start"
            >
                <Radio.Label>
                    <FormattedMessage
                        id="_.topup_choice.reduce_minimum_repayments"
                        defaultMessage="Reduce min. monthly repayments"
                    />
                    <span className="block text-s font-reg">
                        <FormattedMessage
                            id="_.topup_choice.reduce_minimum_repayments_description"
                            defaultMessage="Lower monthly repayments by extending your loan while maintaining the balance."
                        />
                    </span>
                </Radio.Label>
                <Radio.Input
                    name="topup-choice"
                    className="mt-s"
                    onChange={() => {
                        tracking.ampli.radioButtonSelected({
                            account_id: accountId,
                            label: 'Reduce min. monthly repayments'
                        });
                        setSelectedOption('reduce_minimum_repayments');
                    }}
                />
            </Radio.Root>
        </div>
    );
};

const ExistingLoanSummary = ({ account }: { account: TAccount }) => {
    const outstandingBalance = account.pending_balance!.total!;
    const remainingTermMonths = account.remaining_term!.months!;
    const minMonthlyRepayment =
        account.scheduled_payments!.summary!.equal_repayment_amount!;
    const interestRate = getMaxMonthlyInterestRate(account);

    return (
        <Card className="space-y-l min-l:max-w-[375px] min-l:p-xl">
            <h3 className="m-0">
                <FormattedMessage
                    id="_.topup_choice.existing_loan_summary"
                    defaultMessage="Your existing loan summary"
                />
            </h3>
            <div className="w-full space-y-m">
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.topup_choice.outstanding_balance"
                            defaultMessage="Outstanding balance"
                        />
                    </Text>
                    <Text className="font-med">
                        <FormattedCurrency value={outstandingBalance} />
                    </Text>
                </div>
                {minMonthlyRepayment ? (
                    <div className="flex items-center justify-between">
                        <Text>
                            <FormattedMessage
                                id="_.topup_choice.min_monthly_repayment"
                                defaultMessage="Min. monthly repayment"
                            />
                        </Text>
                        <Text className="font-med">
                            <FormattedCurrency value={minMonthlyRepayment} />
                        </Text>
                    </div>
                ) : null}
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.topup_choice.interest_rate_per_30_days"
                            defaultMessage="Interest rate per 30 days"
                        />
                    </Text>
                    <Text className="font-med">
                        <Rate value={interestRate} />
                    </Text>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.topup_choice.remaining_term"
                            defaultMessage="Remaining term"
                        />
                    </Text>
                    <Text className="font-med">
                        <FormattedMessage
                            id="_.topup_choice.remaining_term_months"
                            defaultMessage="{months} months"
                            values={{ months: remainingTermMonths }}
                        />
                    </Text>
                </div>
            </div>
        </Card>
    );
};
